import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence, type Variants } from 'framer-motion';
import { Send, ArrowLeft, Check } from 'lucide-react';
import { requestPasswordReset } from '../services/api';

const EASE = [0.2, 0, 0, 1] as const;

const formContainer: Variants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.06,
      delayChildren: 0.05,
    },
  },
};

const formChild: Variants = {
  hidden: { opacity: 0, y: 6 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.28, ease: EASE },
  },
};

/**
 * Public — request a password-reset email. Shares the visual shell
 * of LoginPage (max-w-[440px], section-label headings, framer-motion
 * stagger).
 *
 * The backend deliberately returns the same 202 response whether
 * the email is on file, the user is inactive, or the rate limit
 * fired — so the UI shows the same generic confirmation regardless.
 * That defends against email enumeration the same way the login
 * endpoint does.
 */
export default function RequestPasswordResetPage() {
  const { t, i18n } = useTranslation();

  const [email, setEmail] = useState('');
  const [emailFocused, setEmailFocused] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!email.trim()) {
      setError(t('auth.requiredFields'));
      return;
    }
    setSubmitting(true);
    try {
      // Forward the SPA's currently-selected i18next locale so the
      // reset email matches what the user just saw in the app. The
      // language string from i18n.language can include region tags
      // (``en-US``); collapse to the base code the backend accepts.
      const baseLang = (i18n.language || '').toLowerCase().split('-')[0];
      const lang =
        baseLang === 'en' || baseLang === 'da'
          ? (baseLang as 'en' | 'da')
          : undefined;
      await requestPasswordReset(email.trim(), lang);
      setSubmitted(true);
    } catch (_err) {
      // Network errors only — the API doesn't surface a user-found
      // signal. Show a generic try-again.
      setError(t('auth.resetRequestNetworkError'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-[440px] mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.24, ease: EASE }}
        className="space-y-3 mb-8"
      >
        <Link
          to="/login"
          className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.14em] text-[var(--color-ink-soft)] hover:text-[var(--color-ink)]"
        >
          <ArrowLeft className="w-3 h-3" strokeWidth={1.5} />
          {t('auth.backToLogin')}
        </Link>
        <div className="section-label">{t('auth.resetSectionLabel')}</div>
        <h1 className="font-mono text-2xl tracking-tight text-[var(--color-ink)]">
          {t('auth.resetTitle')}
        </h1>
        <p className="text-sm text-[var(--color-ink-soft)] leading-relaxed">
          {t('auth.resetLede')}
        </p>
      </motion.div>

      <div className="draw-rule h-px mb-8" aria-hidden />

      {submitted ? (
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.28, ease: EASE }}
          className="border border-[var(--color-rule)] bg-[var(--color-paper)] p-6 space-y-3"
        >
          <div className="flex items-center gap-2">
            <Check
              className="w-3.5 h-3.5 text-[var(--color-ink-muted)]"
              strokeWidth={1.5}
            />
            <span className="section-label">
              {t('auth.resetSubmittedLabel')}
            </span>
          </div>
          <p className="text-sm text-[var(--color-ink)] leading-relaxed">
            {t('auth.resetSubmittedBody', { email: email.trim() })}
          </p>
          <p className="text-xs text-[var(--color-ink-muted)] leading-relaxed">
            {t('auth.resetSubmittedHint')}
          </p>
        </motion.div>
      ) : (
        <motion.form
          variants={formContainer}
          initial="hidden"
          animate="visible"
          onSubmit={handleSubmit}
          className="space-y-6"
        >
          <motion.div variants={formChild}>
            <label
              htmlFor="reset-email"
              className="section-label block mb-2"
            >
              {t('auth.resetEmailLabel')}
            </label>
            <div className="relative">
              <input
                id="reset-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onFocus={() => setEmailFocused(true)}
                onBlur={() => setEmailFocused(false)}
                className="w-full"
                required
              />
              <motion.div
                className="absolute bottom-0 left-0 right-0 h-px bg-[var(--color-ink)]"
                initial={{ scaleX: 0 }}
                animate={{ scaleX: emailFocused ? 1 : 0 }}
                transition={{ duration: 0.22, ease: EASE }}
                style={{ transformOrigin: '0% 50%' }}
              />
            </div>
          </motion.div>

          <motion.div variants={formChild} className="flex items-center gap-3">
            <motion.button
              type="submit"
              disabled={submitting}
              className="btn-primary inline-flex items-center gap-2 px-5 py-2.5 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              animate={
                submitting ? { opacity: [1, 0.7, 1] } : { opacity: 1 }
              }
              transition={
                submitting
                  ? { duration: 1.2, repeat: Infinity, ease: 'easeInOut' }
                  : { duration: 0.15 }
              }
            >
              <Send className="w-4 h-4" strokeWidth={1.5} />
              {submitting
                ? t('auth.resetSubmitting')
                : t('auth.resetSubmit')}
            </motion.button>
          </motion.div>

          <AnimatePresence mode="wait">
            {error && (
              <motion.div
                key={error}
                role="alert"
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 4 }}
                transition={{ duration: 0.18, ease: EASE }}
                className="text-xs font-mono text-[var(--color-alert)]"
              >
                {error}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.form>
      )}
    </div>
  );
}
